import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, map, Observable, shareReplay, throwError } from 'rxjs';

import { AuthenticationService } from './authentication.service';
import { Credentials } from './credentials.service';

/**
 * Point unique de rafraîchissement de l'access token.
 *
 * Le refresh token est tournant côté serveur et invalidé dès qu'un nouveau est
 * émis : deux demandes concurrentes déconnecteraient l'utilisateur, la seconde
 * présentant un jeton déjà mort. Tous les appelants partagent donc la même
 * demande en vol — l'intercepteur HTTP, et le flux SSE de l'agent IA, qui ne
 * passe pas par HttpClient et n'est donc pas intercepté.
 */
@Injectable({
    providedIn: 'root'
})
export class TokenRefreshService {

    private _authenticationService = inject(AuthenticationService);
    private _router = inject(Router);

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    /** Demande en cours, partagée par tous les appelants. `null` si aucune. */
    private _pendingRefresh$: Observable<string> | null = null;

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    /**
     * Renvoie un access token neuf. Si une demande est déjà en vol, l'appelant
     * l'attend et reçoit le même token que les autres.
     *
     * En cas d'échec (refresh token mort ou blacklisté), la session est fermée,
     * la page de connexion rouverte, et l'erreur propagée à tous les appelants.
     */
    public refresh$(): Observable<string> {
        if (!this._pendingRefresh$) {
            this._pendingRefresh$ = this._authenticationService.refreshToken$().pipe(
                map(credentials => (credentials as Credentials).access),
                catchError(error => {
                    this._logout();
                    return throwError(() => error);
                }),
                // Libère la demande en vol : le prochain appelant en ouvrira une neuve.
                finalize(() => this._pendingRefresh$ = null),
                shareReplay({ bufferSize: 1, refCount: false })
            );
        }
        return this._pendingRefresh$;
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _logout(): void {
        this._authenticationService.logout$().subscribe(() => {
            // Reouverture de la page de connexion
            this._router.navigate(['/login']);
        });
    }
}
